import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import Select from 'common/components/select/Select';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { FuelExpensesPerAssetDashboardDto } from 'api/dashboard/dtos/FuelExpensesPerAssetDashboardDto';
import { VehicleFuelKindsDto } from 'api/dashboard/dtos/VehicleFuelKindsDto';
import DashboardService from 'api/dashboard/DashboardService';
import Utils from 'common/services/Utils';
import Logger from 'common/services/Logger';
import { LOGGER_LOG_TYPE } from 'Config';
import useWidgetFilters from '../hooks/useWidgetFilters';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import WidgetNoData from '../components/WidgetNoData';
import KpiHeader from '../components/KpiHeader';
import { FiltersFormModel } from '../filters/FiltersView';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import FuelExpensesPerAssetChart from './FuelExpensesPerAssetChart';
import styles from './FuelExpensesPerAsset.module.scss';

const MIN_VEHICLES = 1;
const MAX_VEHICLES = 5;
const SELECTION_STORAGE_KEY = 'fleetmax_dashboard_fuel_expenses_per_asset_vehicles';

type Props = {
	vehicles?: SelectValueLabel[];
	// Modo "veículo fixo" (detalhe do ativo): bloqueia ao ativo atual e segue o intervalo de datas partilhado.
	lockedVehicleId?: string;
	filters?: FiltersFormModel;
};

const FuelExpensesPerAsset = ({ vehicles = [], lockedVehicleId, filters }: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [selectedVehicles, setSelectedVehicles] = useState<SelectValueLabel[]>([]);
	const [data, setData] = useState<FuelExpensesPerAssetDashboardDto | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [fuelKinds, setFuelKinds] = useState<VehicleFuelKindsDto[] | null>(null);
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('fuelExpensesPerAsset');
	const isLocked = !!lockedVehicleId;
	const effectiveDateFrom = isLocked ? (filters?.dateFrom ?? dateFrom) : dateFrom;
	const effectiveDateTo = isLocked ? (filters?.dateTo ?? dateTo) : dateTo;

	const uniqueVehicles = useMemo(() => {
		const seen = new Set<string>();
		const dedup = vehicles.filter(v => {
			if (seen.has(v.value)) return false;
			seen.add(v.value);
			return true;
		});
		// Despesas de combustível só fazem sentido em ativos com motorização a combustão —
		// elétricos puros ficam fora da dropdown assim que o map de tipos por ativo chega.
		if (!fuelKinds) return dedup;
		const allowed = new Set(
			fuelKinds.filter(k => k.hasCombustion).map(k => k.vehicleId),
		);
		return dedup.filter(v => allowed.has(v.value));
	}, [vehicles, fuelKinds]);

	useEffect(() => {
		let cancelled = false;
		DashboardService.getVehicleFuelKinds()
			.then(rows => {
				if (cancelled) return;
				setFuelKinds(rows ?? []);
			})
			.catch(error => {
				Logger.error(LOGGER_LOG_TYPE.DASHBOARD, 'error getting vehicle fuel kinds', error);
				if (!cancelled) setFuelKinds([]);
			});
		return () => { cancelled = true; };
	}, []);

	useEffect(() => {
		if (isLocked) return;
		// Só restaura a seleção guardada depois do filtro de combustão estar aplicado,
		// para não repor ativos elétricos gravados antes desta regra.
		if (!fuelKinds) return;
		if (uniqueVehicles.length === 0 || selectedVehicles.length > 0) return;
		try {
			const raw = localStorage.getItem(SELECTION_STORAGE_KEY);
			if (!raw) return;
			const savedIds: string[] = JSON.parse(raw);
			const restored = savedIds
				.slice(0, MAX_VEHICLES)
				.map(id => uniqueVehicles.find(v => v.value === id))
				.filter((v): v is SelectValueLabel => v != null);
			if (restored.length >= MIN_VEHICLES) {
				setSelectedVehicles(restored);
			}
		} catch {
			// ignore
		}
		// eslint-disable-next-line
	}, [uniqueVehicles]);

	const fetchData = useCallback(async (vehicleIds: string[]) => {
		if (vehicleIds.length < MIN_VEHICLES) {
			setData(null);
			return;
		}
		try {
			setIsLoading(true);
			const result = await DashboardService.getFuelExpensesPerAsset(vehicleIds, effectiveDateFrom, effectiveDateTo);
			setData(result || null);
		} catch (error) {
			addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			Logger.error(LOGGER_LOG_TYPE.DASHBOARD, 'error getting fuel expenses per asset', error);
		} finally {
			setIsLoading(false);
		}
		// eslint-disable-next-line
	}, [effectiveDateFrom, effectiveDateTo]);

	// Modo comparação (não fixo): segue a seleção do utilizador.
	useEffect(() => {
		if (isLocked) return;
		const ids = selectedVehicles.map(v => v.value);
		fetchData(ids);
	}, [selectedVehicles, fetchData, isLocked]);

	// Modo "veículo fixo": carrega sempre o ativo atual.
	useEffect(() => {
		if (!isLocked || !lockedVehicleId) return;
		fetchData([lockedVehicleId]);
	}, [isLocked, lockedVehicleId, fetchData]);

	const handleVehicleChange = (values: any) => {
		const selected: SelectValueLabel[] = values || [];
		if (selected.length > MAX_VEHICLES) return;
		setSelectedVehicles(selected);
		try {
			localStorage.setItem(SELECTION_STORAGE_KEY, JSON.stringify(selected.map(v => v.value)));
		} catch {
			// ignore
		}
	};

	const periodTotal = useMemo(() => {
		if (!data?.items) return 0;
		return data.items.reduce(
			(acc, vehicle) => acc + vehicle.items.reduce((s, m) => s + (m.value || 0), 0),
			0,
		);
	}, [data]);

	const monthPicker = <WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />;

	const selector = (
		<div className={styles.selectorWrapper}>
			<Select
				options={uniqueVehicles}
				value={selectedVehicles}
				onChange={handleVehicleChange}
				isMulti
				isClearable
				slim
				usePortal
				placeholder={t('dashboard.fuel_expenses_per_asset_select_placeholder' as any, { max: MAX_VEHICLES }) as string}
			/>
			{monthPicker}
		</div>
	);

	const renderBody = () => {
		if (isLoading) {
			return <div className={styles.centerAlign}><LoadingSpinner /></div>;
		}
		if (isLocked) {
			if (!data || data.items.length === 0) {
				return <WidgetNoData />;
			}
		} else {
			if (selectedVehicles.length < MIN_VEHICLES) {
				return <div className={styles.emptyMessage}>{t('dashboard.fuel_expenses_per_asset_select_hint' as any, { max: MAX_VEHICLES }) as string}</div>;
			}
			if (!data) {
				return <div className={styles.centerAlign}><LoadingSpinner /></div>;
			}
		}
		return (
			<>
				<KpiHeader
					value={Utils.formatCurrency(periodTotal)}
					subLabel={t('dashboard.period_total' as any) as string}
					currentTotal={periodTotal}
					previousTotal={null}
					previousRange={null}
				/>
				<FuelExpensesPerAssetChart data={data} />
			</>
		);
	};

	return (
		<Box
			className={tableStyles.container}
			title={t((isLocked ? 'dashboard.fuel_expenses_asset' : 'dashboard.fuel_expenses_per_asset') as any) as string}
			subTitleTooltip={t((isLocked ? 'dashboard.fuel_expenses_asset_tooltip' : 'dashboard.fuel_expenses_per_asset_tooltip') as any) as string}
			renderOptions={isLocked ? monthPicker : selector}
		>
			{renderBody()}
		</Box>
	);
};

export default FuelExpensesPerAsset;
